import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import TransactionModal from './TransactionModal';

interface RevenueTabProps {
  tournamentId: string;
  onRefresh: () => void;
  onAddTransaction: () => void;
}

interface Transaction {
  id: string;
  category: string;
  description: string;
  amount: number;
  transaction_date: string;
  status: string;
  payment_method?: string;
  reference_number?: string;
}

export default function RevenueTab({ tournamentId, onRefresh, onAddTransaction }: RevenueTabProps) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [playerRegistrationRevenue, setPlayerRegistrationRevenue] = useState(0);
  const [partnerContributions, setPartnerContributions] = useState(0);
  const [playerCount, setPlayerCount] = useState(0);
  const [partnerCount, setPartnerCount] = useState(0);
  const [error, setError] = useState('');

  const recordsPerPageOptions = [5, 10, 25, 50];

  useEffect(() => {
    if (tournamentId) {
      fetchTransactions();
      fetchPlayerRegistrationData();
      fetchPartnerContributions();
    }
  }, [tournamentId]);

  useEffect(() => {
    filterTransactions();
  }, [transactions, searchTerm, selectedCategory, selectedStatus]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);

      const { data, error: fetchError } = await supabase
        .from('financial_transactions')
        .select('*')
        .eq('tournament_id', tournamentId)
        .eq('type', 'income')
        .order('transaction_date', { ascending: false });

      if (fetchError) throw fetchError;

      if (data) {
        setTransactions(data);

        const excludedCategories = ['registration', 'sponsorship'];
        const total = data
          .filter(t => t.status === 'completed' && !excludedCategories.includes(t.category?.toLowerCase()))
          .reduce((sum, t) => sum + (t.amount || 0), 0);

        setTotalRevenue(total);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load revenue data');
      toast.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerRegistrationData = async () => {
    try {
      const { data: tournamentData } = await supabase
        .from('tournaments')
        .select('registration_fee')
        .eq('id', tournamentId)
        .maybeSingle();

      const { data: playersWithPayment } = await supabase
        .from('players')
        .select('id, registration_status')
        .eq('tournament_id', tournamentId);

      const registrationFee = tournamentData?.registration_fee || 50000;
      const confirmedPlayers = playersWithPayment?.filter(p => {
        const rs = p.registration_status as { payment?: boolean } | null;
        return rs?.payment === true;
      }).length || 0;

      setPlayerRegistrationRevenue(registrationFee * confirmedPlayers);
      setPlayerCount(confirmedPlayers);
    } catch (err) {
      console.error('Error fetching player registration data:', err);
    }
  };

  const fetchPartnerContributions = async () => {
    try {
      const { data: activePartners } = await supabase
        .from('partners')
        .select('contribution')
        .eq('tournament_id', tournamentId)
        .eq('status', 'active');

      const totalContributions = activePartners?.reduce((sum, p) => sum + (p.contribution || 0), 0) || 0;
      setPartnerContributions(totalContributions);
      setPartnerCount(activePartners?.length || 0);
    } catch (err) {
      console.error('Error fetching partner contributions:', err);
    }
  };

  const filterTransactions = () => {
    let filtered = [...transactions];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        transaction =>
          transaction.description.toLowerCase().includes(term) ||
          transaction.category.toLowerCase().includes(term) ||
          (transaction.reference_number && transaction.reference_number.toLowerCase().includes(term))
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(transaction => transaction.category === selectedCategory);
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(transaction => transaction.status === selectedStatus);
    }

    setFilteredTransactions(filtered);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredTransactions.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredTransactions.slice(indexOfFirstRecord, indexOfLastRecord);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleRecordsPerPageChange = (value: number) => {
    setRecordsPerPage(value);
    setCurrentPage(1);
  };

  const handleEditTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'sponsorship':
        return 'bg-blue-100 text-blue-800';
      case 'registration':
        return 'bg-green-100 text-green-800';
      case 'merchandise':
        return 'bg-teal-100 text-teal-800';
      case 'ticket':
        return 'bg-amber-100 text-amber-800';
      case 'donation':
        return 'bg-rose-100 text-rose-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const categories = ['all', ...new Set(transactions.map(t => t.category))];

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const combinedRevenue = playerRegistrationRevenue + partnerContributions + totalRevenue;

  const excludedCategories = ['registration', 'sponsorship'];
  const hasOverlapRisk = false;

  return (
    <div className="p-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-golf-gray mb-1">Total Revenue</h3>
            <span className="text-3xl font-bold text-golf-gray">{formatCurrency(combinedRevenue)}</span>
            <p className="text-xs text-gray-500 mt-1">Registration Fees + Partner Contributions + Other Transactions</p>
          </div>
          <button
            onClick={onAddTransaction}
            className="modern-button px-4 py-2 text-white font-medium inline-flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Revenue</span>
          </button>
        </div>

<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="bg-blue-100 p-1.5 rounded-lg">
                <ArrowTrendingUpIcon className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-sm text-gray-500">Registration Fees</span>
              <div className="group relative ml-auto">
                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help" />
                <div className="absolute right-0 bottom-full mb-2 w-56 bg-gray-800 text-white text-xs rounded-lg p-2 hidden group-hover:block z-10 shadow-lg">
                  Auto-calculated: tournament registration fee multiplied by number of players with confirmed payment status.
                </div>
              </div>
            </div>
            <p className="text-xl font-bold text-golf-gray">{formatCurrency(playerRegistrationRevenue)}</p>
            <p className="text-xs text-gray-500 mt-1">{playerCount} confirmed player{playerCount !== 1 ? 's' : ''}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="bg-amber-100 p-1.5 rounded-lg">
                <ArrowTrendingUpIcon className="w-4 h-4 text-amber-600" />
              </div>
              <span className="text-sm text-gray-500">Partner Contributions</span>
              <div className="group relative ml-auto">
                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help" />
                <div className="absolute right-0 bottom-full mb-2 w-56 bg-gray-800 text-white text-xs rounded-lg p-2 hidden group-hover:block z-10 shadow-lg">
                  Auto-calculated: sum of the contribution amounts set on all active partners for this tournament.
                </div>
              </div>
            </div>
            <p className="text-xl font-bold text-golf-gray">{formatCurrency(partnerContributions)}</p>
            <p className="text-xs text-gray-500 mt-1">{partnerCount} active partner{partnerCount !== 1 ? 's' : ''}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="bg-green-100 p-1.5 rounded-lg">
                <ArrowTrendingUpIcon className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-sm text-gray-500">Other Transactions</span>
              <div className="group relative ml-auto">
                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help" />
                <div className="absolute right-0 bottom-full mb-2 w-64 bg-gray-800 text-white text-xs rounded-lg p-2 hidden group-hover:block z-10 shadow-lg">
                  Manually entered income transactions (completed status only), excluding Registration and Sponsorship categories which are already counted above. Use this for merchandise, ticket sales, donations, and similar revenue.
                </div>
              </div>
            </div>
            <p className="text-xl font-bold text-golf-gray">{formatCurrency(totalRevenue)}</p>
            <p className="text-xs text-gray-500 mt-1">{transactions.filter(t => t.status === 'completed' && !excludedCategories.includes(t.category?.toLowerCase())).length} completed transaction{transactions.filter(t => t.status === 'completed' && !excludedCategories.includes(t.category?.toLowerCase())).length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-golf-gray mb-4">Revenue by Category</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {(() => {
            const revenueByCat: Record<string, number> = {};
            transactions
              .filter(t => t.status === 'completed' && !excludedCategories.includes(t.category?.toLowerCase()))
              .forEach(t => {
                revenueByCat[t.category] = (revenueByCat[t.category] || 0) + t.amount;
              });

            const entries = Object.entries(revenueByCat);
            if (entries.length === 0) {
              return (
                <div className="col-span-full text-center py-8 text-gray-500">
                  No other revenue categories to display. Registration and Sponsorship revenue is tracked above.
                </div>
              );
            }

            const catTotal = entries.reduce((sum, [, amt]) => sum + amt, 0);

            return entries.map(([category, amount]) => (
              <div key={category} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg ${getCategoryColor(category)}`}>
                    <ArrowTrendingUpIcon className="w-5 h-5" />
                  </div>
                  <h3 className="font-medium text-golf-gray capitalize">{category}</h3>
                </div>
                <p className="text-2xl font-bold text-golf-gray">{formatCurrency(amount)}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {catTotal > 0 ? ((amount / catTotal) * 100).toFixed(1) : 0}% of other transaction revenue
                </p>
              </div>
            ));
          })()}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              />
            </div>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FunnelIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-700">Filters</span>
            <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              >
                <option value="all">All Categories</option>
                {categories
                  .filter(c => c !== 'all')
                  .map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))
                }
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              >
                <option value="all">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Records per page</label>
              <select
                value={recordsPerPage}
                onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              >
                {recordsPerPageOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </motion.div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Show</span>
            <select
              value={recordsPerPage}
              onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
              className="border border-gray-300 rounded-lg text-sm focus:ring-golf-orange focus:border-golf-orange"
            >
              {recordsPerPageOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <span className="text-sm text-gray-500">entries</span>
          </div>
          <div className="text-sm text-gray-500">
            {loading ? (
              'Loading transactions...'
            ) : (
              `Showing ${filteredTransactions.length > 0 ? indexOfFirstRecord + 1 : 0} to ${Math.min(indexOfLastRecord, filteredTransactions.length)} of ${filteredTransactions.length} entries`
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">
            <p>{error}</p>
            <button
              onClick={fetchTransactions}
              className="mt-4 text-golf-orange hover:text-golf-green"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentRecords.length > 0 ? (
                  currentRecords.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full">
                            <div className="bg-green-100 p-2 rounded-full">
                              <ArrowTrendingUpIcon className="h-5 w-5 text-green-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{transaction.description}</div>
                            <div className="text-sm text-gray-500">
                              {transaction.reference_number && `Ref: ${transaction.reference_number}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryColor(transaction.category)}`}>
                          {transaction.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          {formatDate(transaction.transaction_date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-green-600">
                          +{formatCurrency(transaction.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          transaction.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : transaction.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {transaction.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEditTransaction(transaction)}
                          className="text-golf-orange hover:text-golf-green"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No revenue transactions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>

            {getPageNumbers().map((number) => (
              <button
                key={number}
                onClick={() => handlePageChange(number)}
                className={`px-3 py-1 rounded-lg ${
                  currentPage === number
                    ? 'bg-golf-orange text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {number}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`p-2 rounded-lg ${
                currentPage === totalPages || totalPages === 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <TransactionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTransaction(null);
        }}
        transaction={selectedTransaction}
        tournamentId={tournamentId}
        transactionType="income"
        onSuccess={() => {
          fetchTransactions();
          fetchPlayerRegistrationData();
          fetchPartnerContributions();
          onRefresh();
        }}
      />
    </div>
  );
}
